import fs from 'fs-extra';
import path from 'path';
import crypto from 'crypto';

export interface CacheOptions {
  ttl?: number; // Time to live in milliseconds
  cacheDir?: string;
}

export class SimpleCache {
  private cacheDir: string;
  private defaultTTL: number;

  constructor(options: CacheOptions = {}) {
    this.cacheDir = options.cacheDir || path.join(process.cwd(), '.workflow', '.cache');
    this.defaultTTL = options.ttl || 300000; // 5 minutes default
  }

  async get<T>(key: string): Promise<T | null> {
    const hashedKey = this.hashKey(key);
    const cachePath = path.join(this.cacheDir, `${hashedKey}.json`);

    try {
      if (await fs.pathExists(cachePath)) {
        const data = await fs.readJson(cachePath);
        
        // Check if cache is expired
        if (Date.now() - data.timestamp > (data.ttl || this.defaultTTL)) {
          await fs.remove(cachePath);
          return null;
        }
        
        return data.value as T;
      }
    } catch (error) {
      // Cache read error, return null
    }

    return null;
  }

  async set<T>(key: string, value: T, ttl?: number): Promise<void> {
    await fs.ensureDir(this.cacheDir);
    
    const hashedKey = this.hashKey(key);
    const cachePath = path.join(this.cacheDir, `${hashedKey}.json`);
    
    const data = {
      key,
      value,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL
    };
    
    await fs.writeJson(cachePath, data);
  }

  async clear(): Promise<void> {
    if (await fs.pathExists(this.cacheDir)) {
      await fs.emptyDir(this.cacheDir);
    }
  }

  async clearExpired(): Promise<void> {
    if (!await fs.pathExists(this.cacheDir)) {
      return;
    }

    const files = await fs.readdir(this.cacheDir);
    
    for (const file of files) {
      if (file.endsWith('.json')) {
        const filePath = path.join(this.cacheDir, file);
        try {
          const data = await fs.readJson(filePath);
          if (Date.now() - data.timestamp > (data.ttl || this.defaultTTL)) {
            await fs.remove(filePath);
          }
        } catch (error) {
          // Invalid cache file, remove it
          await fs.remove(filePath);
        }
      }
    }
  }

  private hashKey(key: string): string {
    return crypto.createHash('md5').update(key).digest('hex');
  }
}